import { AgentApiError, toSafeMessage } from './agentClient';
import type {
  AgentEvent,
  ApprovalRecord,
  CardAction,
  Citation,
  ImportStatus,
  ImportStepName,
  Pack,
  PricingResult,
  PricingUpdate,
  Rating,
  RecordRef,
  ToolStepStatus,
} from './types';
import { GENERIC_FAILURE } from './wire';

/**
 * What a turn shows, in arrival order. Plan 4 replaces the crew_progress and
 * approval_card renderers; nothing else here changes.
 */
export type Part =
  | { kind: 'text'; agent: string; text: string }
  | {
      kind: 'tool_step';
      agent: string;
      callId: string;
      name: string;
      status: ToolStepStatus;
      summary: string;
    }
  | {
      kind: 'crew_progress';
      stage: string;
      round: number;
      status: string;
      detail: string | null;
    }
  | {
      kind: 'import_progress';
      step: ImportStepName;
      status: ImportStatus;
      detail: string | null;
    }
  | {
      kind: 'approval_card';
      cardKind: string;
      cardId: string;
      title: string;
      actions: string[];
      pack: Pack | null;
      /** kind "pricing_update": the parsed body. */
      pricing?: PricingUpdate;
      /** open -> sending -> closed (started, cancelled or decided). */
      cardState: 'open' | 'sending' | 'closed';
      /** The action that closed it, once the server accepted it. */
      chosen: CardAction | null;
      approval: ApprovalRecord | null;
      /** pricing_update: the result, once the card is confirmed or discarded. */
      pricingResult?: PricingResult;
      cardError: string | null;
    };

export type CardPart = Extract<Part, { kind: 'approval_card' }>;

export type TurnStatus =
  | 'starting'
  | 'streaming'
  | 'reconnecting'
  | 'done'
  | 'error';

/** One question and its answer: one run on the agent service. */
export type Turn = {
  id: string;
  userText: string;
  /** The record on screen when the question was sent. */
  recordRef: RecordRef | null;
  runId?: string;
  parts: Part[];
  /** Once per server number `n`, sorted by `n`. */
  citations: Citation[];
  /** Names of the caller's draft skills this turn used, first seen first. */
  draftSkills: string[];
  status: TurnStatus;
  error?: string;
  rating?: Rating;
};

/** sessionId is the server's (from the first 202); null until then and after reset. */
export type ChatState = { sessionId: string | null; turns: Turn[] };

export type ChatAction =
  | { type: 'send'; turnId: string; text: string; recordRef: RecordRef | null }
  | { type: 'started'; turnId: string; runId: string; sessionId?: string }
  | { type: 'event'; turnId: string; event: AgentEvent }
  | { type: 'reconnecting'; turnId: string }
  | { type: 'finished'; turnId: string }
  // A client-side failure. `error` is whatever a stream or request rejected
  // with, and only toSafeMessage(error) ever reaches the state (never the
  // error's own message).
  | { type: 'failed'; turnId: string; error: unknown }
  | { type: 'rate'; turnId: string; rating: Rating }
  | { type: 'reset' }
  | { type: 'card_sending'; turnId: string; cardId: string }
  | {
      type: 'card_done';
      turnId: string;
      cardId: string;
      action: CardAction;
      approval: ApprovalRecord | null;
      pricing?: PricingResult;
    }
  // Like `failed`: `error` is whatever the card request rejected with, and only
  // toSafeMessage(error) reaches the state.
  | { type: 'card_failed'; turnId: string; cardId: string; error: unknown };

export const initialChatState = (): ChatState => ({
  sessionId: null,
  turns: [],
});

/** True while a turn is still waiting for, or receiving, its answer. */
export const isLive = (status: TurnStatus) =>
  status === 'starting' || status === 'streaming' || status === 'reconnecting';

/** True while a card action is being posted (the hook's send guard ignores messages then). */
export const hasCardSending = (state: ChatState): boolean =>
  state.turns.some((turn) =>
    turn.parts.some(
      (part) => part.kind === 'approval_card' && part.cardState === 'sending',
    ),
  );

export const isBusy = (state: ChatState): boolean => {
  const last = state.turns[state.turns.length - 1];
  return (last !== undefined && isLive(last.status)) || hasCardSending(state);
};

const updateTurn = (
  state: ChatState,
  turnId: string,
  update: (turn: Turn) => Turn,
): ChatState => ({
  ...state,
  turns: state.turns.map((turn) => (turn.id === turnId ? update(turn) : turn)),
});

const updateCard = (
  state: ChatState,
  turnId: string,
  cardId: string,
  update: (card: CardPart) => CardPart,
): ChatState =>
  updateTurn(state, turnId, (turn) => ({
    ...turn,
    parts: turn.parts.map((part) =>
      part.kind === 'approval_card' && part.cardId === cardId
        ? update(part)
        : part,
    ),
  }));

/** A closed card never changes again: late card actions are ignored. */
const updateOpenCard = (
  state: ChatState,
  turnId: string,
  cardId: string,
  update: (card: CardPart) => CardPart,
): ChatState =>
  updateCard(state, turnId, cardId, (card) =>
    card.cardState === 'closed' ? card : update(card),
  );

function applyEvent(turn: Turn, event: AgentEvent): Turn {
  switch (event.type) {
    case 'text': {
      const last = turn.parts[turn.parts.length - 1];
      if (last?.kind === 'text' && last.agent === event.agent) {
        return {
          ...turn,
          parts: [
            ...turn.parts.slice(0, -1),
            { ...last, text: last.text + event.delta },
          ],
        };
      }
      return {
        ...turn,
        parts: [
          ...turn.parts,
          { kind: 'text', agent: event.agent, text: event.delta },
        ],
      };
    }
    case 'tool_step': {
      const step: Part = {
        kind: 'tool_step',
        agent: event.agent,
        callId: event.callId,
        name: event.name,
        status: event.status,
        summary: event.summary,
      };
      // done/error replaces the started row for the same callId, in place.
      const at = turn.parts.findIndex(
        (p) => p.kind === 'tool_step' && p.callId === event.callId,
      );
      if (at === -1) return { ...turn, parts: [...turn.parts, step] };
      return {
        ...turn,
        parts: turn.parts.map((p, i) => (i === at ? step : p)),
      };
    }
    case 'citation':
      if (turn.citations.some((c) => c.n === event.citation.n)) return turn;
      return {
        ...turn,
        citations: [...turn.citations, event.citation].sort(
          (a, b) => a.n - b.n,
        ),
      };
    case 'draft_skills':
      return {
        ...turn,
        draftSkills: [
          ...turn.draftSkills,
          ...event.skills.filter((s) => !turn.draftSkills.includes(s)),
        ],
      };
    case 'crew_progress':
      return {
        ...turn,
        parts: [
          ...turn.parts,
          {
            kind: 'crew_progress',
            stage: event.stage,
            round: event.round,
            status: event.status,
            detail: event.detail,
          },
        ],
      };
    case 'import_progress':
      return {
        ...turn,
        parts: [
          ...turn.parts,
          {
            kind: 'import_progress',
            step: event.step,
            status: event.status,
            detail: event.detail,
          },
        ],
      };
    case 'approval_card':
      return {
        ...turn,
        parts: [
          ...turn.parts,
          {
            kind: 'approval_card',
            cardKind: event.kind,
            cardId: event.cardId,
            title: event.title,
            actions: event.actions,
            pack: event.pack,
            ...(event.pricing ? { pricing: event.pricing } : {}),
            cardState: 'open',
            chosen: null,
            approval: null,
            cardError: null,
          },
        ],
      };
    case 'error':
      return {
        ...turn,
        status: 'error',
        error: event.message,
        runId: turn.runId ?? (event.runId || undefined),
      };
    case 'done':
      return event.status === 'failed'
        ? { ...turn, status: 'error', error: turn.error ?? GENERIC_FAILURE }
        : { ...turn, status: 'done' };
  }
}

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'send':
      return {
        ...state,
        turns: [
          ...state.turns,
          {
            id: action.turnId,
            userText: action.text,
            recordRef: action.recordRef,
            parts: [],
            citations: [],
            draftSkills: [],
            status: 'starting',
          },
        ],
      };
    case 'started':
      // A reply for a turn that is gone (reset while the request was in
      // flight) must not bring the old session back.
      if (!state.turns.some((turn) => turn.id === action.turnId)) return state;
      return {
        ...updateTurn(state, action.turnId, (turn) => ({
          ...turn,
          runId: action.runId,
          status: turn.status === 'starting' ? 'streaming' : turn.status,
        })),
        sessionId: action.sessionId ?? state.sessionId,
      };
    case 'event':
      return updateTurn(state, action.turnId, (turn) => {
        if (!isLive(turn.status)) return turn; // late events after done or error
        const next = applyEvent(turn, action.event);
        return isLive(next.status) ? { ...next, status: 'streaming' } : next;
      });
    case 'reconnecting':
      return updateTurn(state, action.turnId, (turn) =>
        turn.status === 'streaming'
          ? { ...turn, status: 'reconnecting' }
          : turn,
      );
    case 'finished':
      return updateTurn(state, action.turnId, (turn) =>
        isLive(turn.status) ? { ...turn, status: 'done' } : turn,
      );
    case 'failed': {
      const message = toSafeMessage(action.error);
      return updateTurn(state, action.turnId, (turn) =>
        isLive(turn.status)
          ? { ...turn, status: 'error', error: message }
          : turn,
      );
    }
    case 'rate':
      return updateTurn(state, action.turnId, (turn) => ({
        ...turn,
        rating: action.rating,
      }));
    case 'reset':
      return initialChatState();
    case 'card_sending':
      return updateOpenCard(state, action.turnId, action.cardId, (card) => ({
        ...card,
        cardState: 'sending',
        cardError: null,
      }));
    case 'card_done':
      return updateOpenCard(state, action.turnId, action.cardId, (card) => ({
        ...card,
        cardState: 'closed',
        chosen: action.action,
        approval: action.approval,
        ...(action.pricing ? { pricingResult: action.pricing } : {}),
        cardError: null,
      }));
    case 'card_failed': {
      // A 409 card_closed means the card was already used: it stays closed.
      const message = toSafeMessage(action.error);
      const closed =
        action.error instanceof AgentApiError &&
        (action.error.kind === 'card_closed' || action.error.closesCard);
      return updateOpenCard(state, action.turnId, action.cardId, (card) => ({
        ...card,
        cardState: closed ? 'closed' : 'open',
        cardError: message,
      }));
    }
  }
}
